import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, TextButton, useConfirmDialog } from '@safetyhub/ui';
import { adminApi, FeatureFlag, FeatureFlagOverride, FeatureFlagGroupOverride } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import RolloutSlider from './RolloutSlider';

interface FlagListProps {
  flags: FeatureFlag[];
  onSelect: (id: string) => void;
}

export default function FlagList({ flags, onSelect }: FlagListProps) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [confirm, confirmDialog] = useConfirmDialog();

  const toggleMutation = useMutation({
    mutationFn: (id: string) => adminApi.toggleFeatureFlag(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      } else {
        showToast(result.error?.message || '토글에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('토글 중 오류가 발생했습니다.', 'error');
    },
  });

  const rolloutMutation = useMutation({
    mutationFn: ({ id, percentage }: { id: string; percentage: number }) =>
      adminApi.updateFeatureFlagRollout(id, percentage),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      } else {
        showToast(result.error?.message || '롤아웃 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('롤아웃 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteFeatureFlag(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
        showToast('피처 플래그가 보관되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('보관 중 오류가 발생했습니다.', 'error');
    },
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteFeatureFlag(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
        showToast('피처 플래그가 완전 삭제되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('삭제 중 오류가 발생했습니다.', 'error');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => adminApi.restoreFeatureFlag(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
        showToast('피처 플래그가 복구되었습니다.', 'success');
      }
    },
    onError: () => {
      showToast('복구 중 오류가 발생했습니다.', 'error');
    },
  });

  if (flags.length === 0) {
    return (
      <Card padding="xl" radius="lg" className="text-center text-gray-500">
        등록된 피처 플래그가 없습니다.
      </Card>
    );
  }

  return (
    <Card padding="none" radius="lg" className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Key
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Targets
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Enabled
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rollout
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Tags
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {flags.map((flag) => (
            <tr
              key={flag.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelect(flag.id)}
            >
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="font-mono text-sm text-gray-800">{flag.key}</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <span className="text-sm text-gray-900">{flag.name}</span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                <TargetBadges
                  flagId={flag.id}
                  overrides={flag.overrides ?? []}
                  groupOverrides={flag.groupOverrides ?? []}
                />
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                {/* 플래그 *타입*을 색으로 가르는 범례라 blue 유지 — brand(인디고)로 옮기면 purple과 붙는다. */}
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                    flag.type === 'BOOLEAN'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  {flag.type}
                </span>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => toggleMutation.mutate(flag.id)}
                  disabled={toggleMutation.isPending || flag.status === 'ARCHIVED'}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    flag.status === 'ARCHIVED'
                      ? 'bg-gray-200 cursor-not-allowed opacity-50'
                      : flag.enabled
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      flag.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </td>
              <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                <RolloutSlider
                  value={flag.rolloutPercentage}
                  onChange={(percentage) =>
                    rolloutMutation.mutate({ id: flag.id, percentage })
                  }
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {flag.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                  {flag.tags.length > 3 && (
                    <span className="px-1.5 py-0.5 text-gray-400 text-xs">
                      +{flag.tags.length - 3}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                {flag.status === 'ARCHIVED' ? (
                  <div className="flex gap-2 justify-end">
                    <TextButton
                      onClick={() => restoreMutation.mutate(flag.id)}
                      disabled={restoreMutation.isPending}
                      className="text-sm"
                    >
                      복구
                    </TextButton>
                    <TextButton
                      tone="danger"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: '피처 플래그 삭제',
                            body: '이 피처 플래그를 완전히 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.',
                            confirmLabel: '삭제',
                            danger: true,
                          })
                        ) {
                          hardDeleteMutation.mutate(flag.id);
                        }
                      }}
                      disabled={hardDeleteMutation.isPending}
                      className="text-sm"
                    >
                      삭제
                    </TextButton>
                  </div>
                ) : (
                  <TextButton
                    tone="danger"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: '피처 플래그 보관',
                          body: '이 피처 플래그를 보관하시겠습니까?',
                          confirmLabel: '보관',
                          // 보관은 같은 화면의 「복구」로 되돌릴 수 있어 danger가 아니다.
                        })
                      ) {
                        archiveMutation.mutate(flag.id);
                      }
                    }}
                    disabled={archiveMutation.isPending}
                    className="text-sm"
                  >
                    보관
                  </TextButton>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {confirmDialog}
    </Card>
  );
}

function TargetBadges({
  flagId,
  overrides = [],
  groupOverrides = [],
}: {
  flagId: string;
  overrides?: FeatureFlagOverride[];
  groupOverrides?: FeatureFlagGroupOverride[];
}) {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, openAbove: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const toggleOverrideMutation = useMutation({
    mutationFn: ({ overrideId, value }: { overrideId: string; value: boolean }) =>
      adminApi.updateFeatureFlagOverrideValue(flagId, overrideId, value),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      } else {
        showToast(result.error?.message || '값 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('값 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const toggleGroupOverrideMutation = useMutation({
    mutationFn: ({ groupId, value }: { groupId: string; value: boolean }) =>
      adminApi.updateFeatureFlagGroupOverrideValue(flagId, groupId, value),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
      } else {
        showToast(result.error?.message || '값 변경에 실패했습니다.', 'error');
      }
    },
    onError: () => {
      showToast('값 변경 중 오류가 발생했습니다.', 'error');
    },
  });

  const isMutationPending = toggleOverrideMutation.isPending || toggleGroupOverrideMutation.isPending;

  const manualOverrides = overrides.filter((o) => !o.groupLinkId);
  const userOverrides = manualOverrides.filter((o) => o.targetType === 'USER');
  const orgOverrides = manualOverrides.filter((o) => o.targetType === 'ORGANIZATION');

  const userCount = userOverrides.length;
  const orgCount = orgOverrides.length;
  const groupCount = groupOverrides.length;
  const total = userCount + orgCount + groupCount;

  const handleEnter = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (total > 0 && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < 280;
      setPos({
        top: openAbove ? rect.top + window.scrollY : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        openAbove,
      });
      setShow(true);
    }
  }, [total]);

  const handleLeave = useCallback(() => {
    if (isMutationPending) return;
    hideTimerRef.current = setTimeout(() => setShow(false), 150);
  }, [isMutationPending]);

  useEffect(() => {
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, []);

  if (total === 0) {
    return <span className="text-gray-300 text-sm">&mdash;</span>;
  }

  const tooltip = show
    ? createPortal(
        <div
          className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-card shadow-lg"
          style={{
            top: pos.openAbove ? undefined : pos.top,
            bottom: pos.openAbove ? `${window.innerHeight - pos.top + 4}px` : undefined,
            left: pos.left,
          }}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <div className="max-h-60 overflow-y-auto p-3 space-y-3 text-xs">
            {userCount > 0 && (
              <div>
                <div className="font-semibold text-gray-500 mb-1">사용자 ({userCount})</div>
                {userOverrides.map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-0.5">
                    <span className="text-gray-700 truncate mr-2">
                      {o.targetName || o.targetId}
                      {o.targetEmail && (
                        <span className="text-gray-400 ml-1">({o.targetEmail})</span>
                      )}
                    </span>
                    <ToggleableValueBadge
                      value={o.value}
                      isPending={toggleOverrideMutation.isPending}
                      onClick={() => toggleOverrideMutation.mutate({ overrideId: o.id, value: !o.value })}
                    />
                  </div>
                ))}
              </div>
            )}
            {orgCount > 0 && (
              <div>
                <div className="font-semibold text-gray-500 mb-1">조직 ({orgCount})</div>
                {orgOverrides.map((o) => (
                  <div key={o.id} className="flex items-center justify-between py-0.5">
                    <span className="text-gray-700 truncate mr-2">{o.targetName || o.targetId}</span>
                    <ToggleableValueBadge
                      value={o.value}
                      isPending={toggleOverrideMutation.isPending}
                      onClick={() => toggleOverrideMutation.mutate({ overrideId: o.id, value: !o.value })}
                    />
                  </div>
                ))}
              </div>
            )}
            {groupCount > 0 && (
              <div>
                <div className="font-semibold text-gray-500 mb-1">그룹 ({groupCount})</div>
                {groupOverrides.map((g) => (
                  <div key={g.id} className="flex items-center justify-between py-0.5">
                    <span className="text-gray-700 truncate mr-2">
                      {g.groupName}
                      {g.memberCount != null && (
                        <span className="text-gray-400 ml-1">({g.memberCount}명)</span>
                      )}
                    </span>
                    <ToggleableValueBadge
                      value={g.value}
                      isPending={toggleGroupOverrideMutation.isPending}
                      onClick={() => toggleGroupOverrideMutation.mutate({ groupId: g.groupId, value: !g.value })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div
      ref={containerRef}
      className="inline-flex gap-1.5"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {/* 대상 타입 범례(사용자=blue·조직=amber·그룹=green) — OverrideList의 LABEL_MAP과 같은 체계라 유지. */}
      {userCount > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          {userCount}
        </span>
      )}
      {orgCount > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium">
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
          </svg>
          {orgCount}
        </span>
      )}
      {groupCount > 0 && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">
          <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v1h8v-1zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-1a5.97 5.97 0 00-.75-2.9A3.005 3.005 0 0119 17v1h-3zM4.75 14.1A5.97 5.97 0 004 17v1H1v-1a3 3 0 013.75-2.9z" />
          </svg>
          {groupCount}
        </span>
      )}
      {tooltip}
    </div>
  );
}

function ToggleableValueBadge({
  value,
  isPending,
  onClick,
}: {
  value: unknown;
  isPending: boolean;
  onClick: () => void;
}) {
  const isTrue = value === true || value === 'true';
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={isPending}
      className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity disabled:opacity-50 ${
        isTrue ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}
      title="클릭하여 값 토글"
    >
      {isTrue ? 'ON' : 'OFF'}
    </button>
  );
}
